// src/services/ai.js
import { API_BASE } from "./api";

//** ========= NAMA GRADING LOGIC (MANHEIM UK - EXACT RULES) ========= */

// Helper: Check if it's a BODY PANEL (exterior only, visible from 2m)
function isBodyPanel(part) {
  const p = (part || "").toLowerCase().trim();

  // ❌ EXCLUDE: Interior (not visible from 2m outside)
  if (p.includes("door pad") || p.includes("seat") ||
    p.includes("visor") || p.includes("carpet") ||
    p.includes("handle inner") || p.includes("fascia") ||
    p.includes("console") || p.includes("tailgate pad") ||
    p.includes("switches") || p.includes("entertainment") ||
    p.includes("steering wheel") || p.includes("dash") ||
    p.includes("glove box") || p.includes("roof lining") ||
    p.includes("jack") || p.includes("tools") || p.includes("wheel brace") ||
    p.includes("tyre inflation") || p.includes("load area carpet") ||
    p.includes("rear panel trim int") || p.includes("shelfload cover")) {
    return false;
  }

  // ❌ EXCLUDE: Glass (recorded separately, not graded as panels)
  if (p.includes("screen") || p.includes("glass") ||
    p.includes("window") || p.includes("sunroof") ||
    p.includes("qtr light")) {
    return false;
  }

  // ❌ EXCLUDE: Lights/Lamps (not panels)
  if (p.includes("lamp") || p.includes("light") ||
    p.includes("headlamp") || p.includes("fog lamp") ||
    p.includes("flasher")) {
    return false;
  }

  // ❌ EXCLUDE: Small trims/accessories (not significant panels)
  if (p.includes("badge") || p.includes("toweye") ||
    p.includes("reflector") || p.includes("number plate") ||
    p.includes("washer") || p.includes("sensor") ||
    p.includes("aerial") || p.includes("exhaust") ||
    p.includes("charging port") || p.includes("fuel flap")) {
    return false;
  }

  // ❌ EXCLUDE: Wheels/Tyres (not body panels)
  if (p.includes("wheel") || p.includes("tyre") || p.includes("tire")) {
    return false;
  }

  // ❌ EXCLUDE: Door/panel trims (not the panel itself)
  if (p.includes("moulding") || p.includes("trim panel") ||
    p.includes("aperture seal")) {
    return false;
  }

  // ❌ EXCLUDE: Mirrors (glass + housing, not body panels)
  if (p.includes("mirror")) {
    return false;
  }

  // ❌ EXCLUDE: Handles/Locks (not panels)
  if (p.includes("handle") || p.includes("lock")) {
    return false;
  }

  // ❌ EXCLUDE: Arch extensions (not main panel)
  if (p.includes("arch extension")) {
    return false;
  }

  // ✅ INCLUDE: Actual BODY PANELS (major exterior panels)
  return p.includes("bonnet") ||
    p.includes("roof") ||
    p.includes("door nsf") || p.includes("door osf") ||
    p.includes("door nsr") || p.includes("door osr") ||
    p.includes("wing nsf") || p.includes("wing osf") ||
    p.includes("bumper front") || p.includes("bumper rear") ||
    p.includes("panel front") || p.includes("panel rear") ||
    p.includes("tailgate") ||
    p.includes("qtr panel nsr") || p.includes("qtr panel osr") ||
    p.includes("a post") || p.includes("b post") ||
    p.includes("c post") || p.includes("d post");
}

// Helper: Normalize panel names
function normalizePanel(part) {
  const p = String(part || "").toLowerCase().trim();

  if (p.includes("bonnet")) return "Bonnet";
  if (p.includes("roof") && !p.includes("glass")) return "Roof";
  if (p.includes("bumper front")) return "Bumper Front";
  if (p.includes("bumper rear")) return "Bumper Rear";
  if (p.includes("door nsf")) return "Door nsf";
  if (p.includes("door osf")) return "Door osf";
  if (p.includes("door nsr")) return "Door nsr";
  if (p.includes("door osr")) return "Door osr";
  if (p.includes("wing nsf")) return "Wing nsf";
  if (p.includes("wing osf")) return "Wing osf";
  if (p.includes("qtr panel nsr")) return "Qtr Panel nsr";
  if (p.includes("qtr panel osr")) return "Qtr Panel osr";
  if (p.includes("panel front")) return "Panel Front";
  if (p.includes("panel rear")) return "Panel Rear";
  if (p.includes("tailgate")) return "Tailgate";
  if (p.includes("a post")) return "A Post";
  if (p.includes("b post")) return "B Post";
  if (p.includes("c post")) return "C Post";
  if (p.includes("d post")) return "D Post";

  return null;
}

// Helper: Check if paint damage is MAJOR (>25mm panel or >100mm bumper)
function isPaintMajor(damage, detail, part) {
  const dmg = (damage || "").toLowerCase().trim();
  const det = (detail || "").toLowerCase().trim();
  const partLower = (part || "").toLowerCase().trim();

  const isBumper = partLower.includes("bumper");

  const isPaintDamage = dmg.includes("scratch") ||
    dmg.includes("scuff") ||
    dmg.includes("chip");

  if (!isPaintDamage) return false;

  // Bumper threshold: >100mm
  if (isBumper) {
    return det.includes("over 100mm") || det.includes(">100mm");
  }

  // Regular panel threshold: >25mm
  return det.includes("over 25mm") || det.includes(">25mm");
}

// Helper: Check if dent is MAJOR (>30mm)
function isDentMajor(damage, detail) {
  const dmg = (damage || "").toLowerCase().trim();
  const det = (detail || "").toLowerCase().trim();

  if (!dmg.includes("dent")) return false;

  return det.includes("over 30mm") || det.includes(">30mm");
}

// Helper: Check if crack is MAJOR (>30% of panel)
function isCrackMajor(damage, detail) {
  const dmg = (damage || "").toLowerCase().trim();
  const det = (detail || "").toLowerCase().trim();

  const isCrackDamage = dmg.includes("crack") || dmg.includes("split");

  if (!isCrackDamage) return false;

  // "Over 30% Of Panel" or "Replace" = major structural
  return det.includes("over 30%") || det.includes(">30%") || det.includes("replace");
}

// Helper: Check if hole is MAJOR (>10mm)
function isHoleMajor(damage, detail) {
  const dmg = (damage || "").toLowerCase().trim();
  const det = (detail || "").toLowerCase().trim();

  if (!dmg.includes("hole") && !dmg.includes("holed")) return false;

  // Threshold: >10mm for body panels, >3mm for interior (but we ignore interior)
  return det.includes("over 10mm") || det.includes(">10mm");
}

// Helper: Check if part needs replacement
function isMissingOrReplace(damage, detail) {
  const dmg = (damage || "").toLowerCase().trim();
  const det = (detail || "").toLowerCase().trim();

  return dmg.includes("missing") || det.includes("replace") || dmg.includes("broken");
}

// Helper: Check if panel is structural (Roof & Rear Quarters per NAMA)
function isStructuralPanel(part) {
  const p = (part || "").toLowerCase().trim();

  // NAMA defines: "structural panel* (*roof & rear quarters)"
  return p.includes("roof") ||
    p.includes("qtr panel") ||
    p.includes("quarter panel");
}

// Helper: Check if misalignment is significant
function isMisalignmentMajor(damage, detail) {
  const dmg = (damage || "").toLowerCase().trim();
  const det = (detail || "").toLowerCase().trim();

  const isMisalignDamage = dmg.includes("misaligned") || dmg.includes("misalign") ||
    dmg.includes("uneven") || dmg.includes("gap");

  if (!isMisalignDamage) return false;

  // Moderate or Severe = Major issue
  return det.includes("moderate") || det.includes("severe") ||
    det.includes("10-20mm") || det.includes("over 20mm") ||
    det.includes("both sides") || det.includes("multiple");
}

// Helper: Check if poor previous repair (always counts as significant)
function isPoorRepair(damage) {
  const dmg = (damage || "").toLowerCase().trim();
  return dmg.includes("poor previous repair");
}

// ========= MAIN NAMA GRADING FUNCTION =========

export function manheimGradeFromFaults(faults = []) {
  const paintPanels = new Set();
  const dentPanels = new Set();
  const crackPanels = new Set();
  const misalignmentPanels = new Set();  // NEW: Track misaligned panels
  const poorRepairPanels = new Set();     // NEW: Track poor previous repairs

  let structuralCracks = 0;
  let significantTrims = 0; // Count significant trims/parts
  let totalSignificantFaults = 0; // NEW: Count ALL significant faults

  for (const fault of faults) {
    if (!fault || !fault.part) continue;

    // Count ALL faults for total count (regardless of type)
    totalSignificantFaults++;

    // ✅ NEW: Check misalignment (applies to bumpers mainly)
    if (isMisalignmentMajor(fault.damage, fault.detail)) {
      const panel = normalizePanel(fault.part) || fault.part;
      misalignmentPanels.add(panel);
    }

    // ✅ NEW: Check poor previous repairs (always significant)
    if (isPoorRepair(fault.damage)) {
      const panel = normalizePanel(fault.part) || fault.part;
      poorRepairPanels.add(panel);
    }

    // ✅ ONLY process BODY PANELS for NAMA criteria (exterior visible from 2m)
    if (!isBodyPanel(fault.part)) {
      // Count as trim if missing/broken
      if (isMissingOrReplace(fault.damage, fault.detail)) {
        significantTrims++;
      }
      continue; // Skip non-body panels for standard grading
    }

    const panel = normalizePanel(fault.part);
    if (!panel) continue;

    // Paint defects (>25mm panel or >100mm bumper)
    if (isPaintMajor(fault.damage, fault.detail, fault.part)) {
      paintPanels.add(panel);
    }

    // Dents (>30mm)
    if (isDentMajor(fault.damage, fault.detail)) {
      dentPanels.add(panel);
    }

    // Cracks (>30% of panel)
    if (isCrackMajor(fault.damage, fault.detail)) {
      crackPanels.add(panel);

      // Count structural cracks separately
      if (isStructuralPanel(fault.part)) {
        structuralCracks++;
      }
    }

    // Missing body panels
    if (isMissingOrReplace(fault.damage, fault.detail)) {
      significantTrims++; // Missing body panel = significant
    }
  }

  const paintCount = paintPanels.size;
  const dentCount = dentPanels.size;
  const crackCount = crackPanels.size;
  const misalignCount = misalignmentPanels.size;  // NEW
  const poorRepairCount = poorRepairPanels.size;  // NEW

  console.log("🔍 NAMA Grading Analysis:", {
    totalFaults: totalSignificantFaults,
    paintPanels: paintCount,
    dentPanels: dentCount,
    crackPanels: crackCount,
    misalignmentPanels: misalignCount,
    poorRepairPanels: poorRepairCount,
    structuralCracks,
    significantTrims
  });

  // ========= NAMA GRADE RULES (UPDATED WITH NEW DAMAGE TYPES) =========

  // GRADE U - Uneconomical
  if (
    crackCount > 10 ||                    // Exceeds Grade 5 criteria
    structuralCracks >= 2 ||              // Multiple structural damage
    significantTrims > 10 ||              // Multiple significant parts missing
    totalSignificantFaults > 15           // NEW: Too many faults overall
  ) {
    return "U";
  }

  // GRADE 5 - Very Poor
  if (
    crackCount >= 2 ||                    // 2+ panels with significant cracks/damage >30%
    dentCount >= 8 ||                     // 8+ panels with dents >30mm
    paintCount >= 11 ||                   // 11+ panels with paint >25mm (or >100mm bumper)
    structuralCracks >= 1 ||              // 1 structural panel with damage >30%
    misalignCount >= 3 ||                 // NEW: 3+ misaligned panels
    poorRepairCount >= 3 ||               // NEW: 3+ poor previous repairs
    totalSignificantFaults >= 12          // NEW: 12+ total faults
  ) {
    return "5";
  }

  // GRADE 4 - Poor
  if (
    crackCount >= 1 ||                    // 1 panel with significant crack >30%
    (dentCount >= 4 && dentCount <= 7) || // 4-7 panels with dents >30mm
    (paintCount >= 6 && paintCount <= 10) || // 6-10 panels with paint >25mm
    misalignCount >= 2 ||                 // NEW: 2+ misaligned panels
    poorRepairCount >= 2 ||               // NEW: 2+ poor previous repairs
    totalSignificantFaults >= 8           // NEW: 8-11 total faults
  ) {
    return "4";
  }

  // GRADE 3 - Average
  if (
    (paintCount >= 1 && paintCount <= 5) || // 1-5 panels with paint >25mm
    (dentCount >= 1 && dentCount <= 3) ||   // 1-3 panels with dents >30mm
    significantTrims >= 2 ||                 // 2+ significant trims required
    misalignCount >= 1 ||                   // NEW: 1+ misaligned panel
    poorRepairCount >= 1 ||                 // NEW: 1+ poor previous repair
    totalSignificantFaults >= 5             // NEW: 5-7 total faults
  ) {
    return "3";
  }

  // GRADE 2 - Good
  // "Grade 1 plus 1 of the following: paint >25mm, dent >30mm, or significant trim"
  if (
    paintCount === 1 ||
    dentCount === 1 ||
    significantTrims === 1 ||
    totalSignificantFaults >= 3             // NEW: 3-4 total faults
  ) {
    return "2";
  }

  // GRADE 1 - Excellent
  // Minor defects only (dents ≤30mm, scratches ≤25mm, chips ≤10mm)
  // Also requires less than 3 total faults
  return "1";
}


export async function aiGradeFromFaults({ exterior = [], interior = [] }) {
  const allFaults = [...(exterior || []), ...(interior || [])];
  return manheimGradeFromFaults(allFaults);
}

export async function aiSummarizeAppraisal(appraisal) {
  try {
    const res = await fetch(`${API_BASE}/api/ai/summarize`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(appraisal),
    });

    if (!res.ok) throw new Error("Summarize API failed");

    const json = await res.json();
    return (json.summary || "AI summary unavailable.").trim();
  } catch (err) {
    console.error("aiSummarizeAppraisal failed:", err);
    return "AI summary unavailable.";
  }
}
